// Servicio local para gestionar el perfil del operario en el frontend
// y determinar si necesita completar/actualizar información.

// Tipo que representa los datos básicos del operario que vamos a solicitar en el formulario
export type PerfilOperario = {
  // Nivel educativo seleccionado por el operario
  escolaridad?: string;
  // Ciudad seleccionada por el operario
  ciudad?: string;
  // Barrio seleccionado, dependiente de la ciudad
  barrio?: string;
  // Dirección que el operario escribe manualmente
  direccion?: string;
  // Coordenada de latitud, puede ser capturada automáticamente o ingresada manualmente
  latitud?: string;
  // Coordenada de longitud, puede ser capturada automáticamente o ingresada manualmente
  longitud?: string;
  // Nombre del contacto de emergencia
  contactoNombre?: string;
  // Teléfono del contacto de emergencia
  contactoTelefono?: string;
  // agregar el correo electrónico personal del operario
  emailPersonal?: string; // Correo electrónico personal
  // Movilidad propia del operario: puede seleccionar varias opciones
  // Se almacena como arreglo de strings: ["Moto", "Auto"] o ["No"].
  movilidad?: string[];
  // Marca de tiempo (ISO) de la última vez que el usuario guardó sus datos
  updatedAtIso?: string;
};

// Tipo que representa el resultado de la validación del perfil
export type EstadoDatosOperario = {
  // Indica si falta información y, por lo tanto, se debe mostrar el aviso en Home
  needsUpdate: boolean;
  // Lista de motivos legibles que explican qué falta completar
  reasons: string[];
};

// Clave de almacenamiento para persistir el perfil en el navegador del usuario
const STORAGE_KEY_PERFIL = 'operario:perfil:v1';
const STORAGE_KEY_SNOOZE_PREFIX = 'actualizaDatos:snooze:'; // Prefijo para recordar "Más tarde"
const DEFAULT_SNOOZE_DAYS = 7; // Días por defecto para posponer

// Devuelve el perfil del operario desde localStorage (o un objeto vacío si no existe)
export function getPerfil(): PerfilOperario {
  // Leemos el valor crudo de localStorage
  const raw = localStorage.getItem(STORAGE_KEY_PERFIL);
  // Si existe, lo parseamos a JSON; si no, devolvemos un objeto vacío
  return raw ? (JSON.parse(raw) as PerfilOperario) : {};
}

// Persiste el perfil del operario en localStorage agregando la marca de tiempo de actualización
export function savePerfil(perfil: PerfilOperario): void {
  // Agregamos/actualizamos el timestamp para saber cuándo se guardaron los datos
  const withTimestamp: PerfilOperario = { ...perfil, updatedAtIso: new Date().toISOString() };
  // Guardamos el JSON serializado en localStorage
  localStorage.setItem(STORAGE_KEY_PERFIL, JSON.stringify(withTimestamp));
}

// Calcula si el perfil necesita actualización y construye la lista de motivos
export function computeEstado(perfil: PerfilOperario): EstadoDatosOperario {
  // Creamos un arreglo que acumulará los mensajes para el usuario
  const reasons: string[] = [];

  // Validamos cada uno de los campos requeridos y agregamos el motivo si falta
  if (!perfil.escolaridad) reasons.push('Falta seleccionar la escolaridad');
  if (!perfil.ciudad) reasons.push('Falta seleccionar la ciudad');
  if (!perfil.barrio) reasons.push('Falta seleccionar el barrio');
  if (!perfil.direccion) reasons.push('Falta completar la dirección');
  if (!perfil.latitud || !perfil.longitud) reasons.push('Falta capturar las coordenadas');
  if (!perfil.contactoNombre) reasons.push('Falta el nombre del contacto');
  if (!perfil.contactoTelefono) reasons.push('Falta el teléfono del contacto');
  // validar presencia del correo electrónico personal
  if (!perfil.emailPersonal) reasons.push('Falta el correo electrónico personal');
  // Validamos movilidad: debe existir al menos una selección
  if (!perfil.movilidad || perfil.movilidad.length === 0) reasons.push('Falta indicar si cuenta con movilidad propia');

  // El banner se debe mostrar si hay al menos un motivo
  return { needsUpdate: reasons.length > 0, reasons };
}

// Devuelve true si el usuario decidió "Más tarde" y el plazo aún no expiró
export function isSnoozed(operarioId: string): boolean {
  // Construimos la clave específica por operario
  const key = STORAGE_KEY_SNOOZE_PREFIX + operarioId;
  // Leemos hasta cuándo se debe ocultar el banner
  const untilIso = localStorage.getItem(key);
  // Si no hay registro, no está pospuesto
  if (!untilIso) return false;
  // Comparamos la fecha almacenada con el momento actual
  return new Date(untilIso).getTime() > Date.now();
}

// Crea/actualiza un snooze por N días a partir de ahora
export function snooze(operarioId: string, days: number = DEFAULT_SNOOZE_DAYS): void {
  // Construimos la clave específica por operario
  const key = STORAGE_KEY_SNOOZE_PREFIX + operarioId;
  // Calculamos la fecha futura sumando los días indicados
  const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  // Persistimos en localStorage la fecha en formato ISO
  localStorage.setItem(key, until.toISOString());
}
